import { selectAdvancedMarkupTemplateIds } from "@faro-lotv/project-source";
import { getMarkupAssigneeMember } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getMarkupCreator } from "@pages/project-details/project-markups/markup-details-utils";
import { createSelector, lruMemoize } from "@reduxjs/toolkit";
import { getMarkups } from "@store/markups/markups-selector-utils";
import { companyMembersSelector } from "@store/members/members-selector";
import { projectMembersSelector } from "@store/projects/projects-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { areMarkupAssigneesUsed, areMarkupDueDatesUsed, areMarkupStatusesUsed, getUniqueMarkupProperties } from "@utils/markups-utils";

/**
 * @returns the list of all markup entities of the selected project
 *
 * It filters out markups that have integrations with an external service like Bim360 or Procore
 */
export const markupsSelector = createSelector([state => state], state => getMarkups(state), {
  // Building the markups is expensive and creates a new array, so it should only run again
  // when the parts of the state read by `getMarkups` change, and not on every store update.
  argsMemoize: lruMemoize,
  argsMemoizeOptions: {
    equalityCheck: (previousState, state) => previousState.iElements === state.iElements && previousState.slideNodes === state.slideNodes && previousState.projects.selectedProject === state.projects.selectedProject
  }
});

/**
 * @returns the markups of the selected project that are assigned to the current user
 */
export const currentUserMarkupsSelector = createSelector(markupsSelector, currentUserSelector, (markups, currentUser) => {
  if (!currentUser) {
    return [];
  }

  // Only the first assignee is considered, same as the assignee filter of the annotations table
  return markups.filter(markup => markup.assignee?.values?.[0] === currentUser.identity);
});

/**
 * @returns the markups of the selected project that were created by the current user
 */
export const currentUserCreatedMarkupsSelector = createSelector(markupsSelector, currentUserSelector, (markups, currentUser) => currentUser ? markups.filter(markup => markup.createdBy === currentUser.identity) : []);

/** @returns whether any markup of the selected project has a status other than unclassified */
export const areMarkupStatusesUsedSelector = createSelector(markupsSelector, areMarkupStatusesUsed);

/** @returns whether any markup of the selected project has a due date */
export const areMarkupDueDatesUsedSelector = createSelector(markupsSelector, areMarkupDueDatesUsed);

/** @returns whether any markup of the selected project has an assignee */
export const areMarkupAssigneesUsedSelector = createSelector(markupsSelector, areMarkupAssigneesUsed);

/**
 * @returns The template ids for specific project
 */
export const markupsTemplateIdsSelector = createSelector(selectAdvancedMarkupTemplateIds, advancedMarkupTemplateIds => {
  return advancedMarkupTemplateIds;
});

/** Gets a markup entity by providing the markup ID */
export function getMarkupByIdSelector(id) {
  return createSelector(markupsSelector, markups => {
    return markups.find(markup => markup.id === id);
  });
}

/** Returns the unique values of the provided markup key */
export function markupsUniqueKeysSelector(uniqueIdKey) {
  return createSelector(markupsSelector, markups => {
    return getUniqueMarkupProperties({
      markups,
      uniqueIdKey
    });
  });
}

/** @returns the members who created the markups of the selected project, once each */
export const markupsUniqueCreatorsSelector = createSelector(markupsSelector, companyMembersSelector, projectMembersSelector, currentUserSelector, (markups, companyMembers, projectMembers, currentUser) => {
  const creatorIds = new Set(markups.map(({
    createdBy
  }) => createdBy));
  return [...creatorIds].flatMap(creatorId => {
    const creator = getMarkupCreator({
      creatorId,
      companyMembers,
      projectMembers
    }) ?? (
    // The member lists may miss the current user, e.g. without permission to see the workspace members
    creatorId === currentUser?.identity ? currentUser : undefined);
    return creator ? [creator] : [];
  });
});

/** Returns the unique assignees of the existing markups in the type of SdbMemberTypes */
export function markupsUniqueAssigneeSelector() {
  return createSelector(markupsSelector, companyMembersSelector, projectMembersSelector, (markups, companyMembers, projectMembers) => {
    const markupsAssignees = getUniqueMarkupProperties({
      markups,
      uniqueIdKey: "assignee"
    });

    // Markup assignees each have its own id. They might point to the same member by the id stored in "value" key.
    // So we need to check for the unique ones and get the SdbMemberTypes
    return markupsAssignees.reduce((uniqueMembers, member) => {
      const assigneeMember = getMarkupAssigneeMember({
        assignee: member,
        companyMembers,
        projectMembers
      });
      const isMemberExist = uniqueMembers.some(uniqueMember => uniqueMember.id === assigneeMember?.id);
      if (!isMemberExist && assigneeMember) {
        uniqueMembers.push(assigneeMember);
      }
      return uniqueMembers;
    }, []);
  });
}